import { useCallback, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import {
  LazadaStandaloneAuthResultBody,
  generateMercadoAuthUrl,
  mercadoStandaloneResult,
} from 'services/api/admin';
import { useRequest } from 'ahooks';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useTransAppProductName } from 'hooks/useTrans';
import useOpenURL from './useOpenURL';
import posthog from '@leyan/lytics';

export interface MercadoAuthFormValues {
  shopType?: IM_API.Admin.StoreType;
  region?: General.UpperCaseRegion;
}

export const useMercadoAuthShop = () => {
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const [mercadoFormValues, setMercadoFormValues] = useState<MercadoAuthFormValues>();
  const [uuids, setUuids] = useState<string[]>([]);
  const [mercadoResult, setMercadoResult] = useState<LazadaStandaloneAuthResultBody>();

  const openURL = useOpenURL();

  const {
    data: authUrl,
    loading,
    runAsync: getMercadoAuthURL,
  } = useRequest(
    async (departmentIds: number[]) => {
      if (mercadoFormValues?.shopType) {
        const { shopType, region } = mercadoFormValues;
        const uuid = uuidV4();
        const authURL = await generateMercadoAuthUrl(shopType, departmentIds, uuid, region);
        setUuids((u) => [...u, uuid]);
        return authURL;
      }
    },
    {
      manual: true,
      onError() {
        message.error(t('authShop.获取授权链接失败'));
      },
    },
  );

  const copyMercadoURL = useCallback(
    async (departmentIds: number[], noPrefix = false) => {
      try {
        const authURL = await getMercadoAuthURL(departmentIds);
        if (authURL) {
          const template = noPrefix
            ? authURL
            : appProductNameT('authShop.copyLinkTemplate3', { platform: 'Mercado' }) + authURL;
          await navigator.clipboard.writeText(template);
          message.success(t('authShop.复制成功'));
          return true;
        }
        throw new Error('请求失败');
      } catch (err) {
        message.error(t('authShop.复制链接失败'));
        return false;
      }
    },
    [getMercadoAuthURL, t, appProductNameT],
  );

  const openMercadoAuthUrl = useCallback(
    async (departmentIds: number[]) => {
      try {
        const authURL = await getMercadoAuthURL(departmentIds);
        if (authURL) {
          openURL(authURL, 'external');
          return true;
        }
        throw new Error('请求失败');
      } catch (err) {
        message.error(t('authShop.打开链接失败'));
        return false;
      }
    },
    [getMercadoAuthURL, openURL, t],
  );

  const checkMercadoAuthResult = useCallback((uuid: string) => {
    return mercadoStandaloneResult(uuid)
      .then((r) => r.data.data)
      .catch((e) => {
        posthog.capture('$authReulstError', {
          platform: 'mercado',
          error: e,
        });
        return null;
      });
  }, []);

  return {
    authUrl,
    uuids,
    setUuids,
    loading,
    mercadoFormValues,
    setMercadoFormValues,
    copyMercadoURL,
    openMercadoAuthUrl,
    checkMercadoAuthResult,
    mercadoResult,
    setMercadoResult,
  };
};
